<!--
  Shopper payment remediation -- "pay for this order" from an emailed link.

  Plan: docs/planning/2026-09-23-shopper-payment-remediation-plan.md (unit 4).

  WHO THIS IS FOR: a shopper with NO ACCOUNT. Guest checkout is the default,
  so the recipient of the "we could not charge your card" email usually cannot
  log in. The token in the query string IS the authorization -- the route
  deliberately carries no `beforeEnter` guard (Jake's decision 4). Do not add
  `requirePersonalShopperSession` here; it redirects anonymous storefront
  sessions to store sign-in, which breaks this page for its primary audience.

  TWO PATHS REACH IT:
    * Path A -- the saved card was declined or became unusable. The amount
      never changed, so no consent step.
    * Path B -- a $0 freebie order a distributor later edited above $0. The
      shopper agreed to $0.00 and is now being asked for money, so they must
      APPROVE the new amount before the card form does anything.

  THE PLATFORM STRIPE INSTANCE IS LOAD-BEARING. The SetupIntent is created on
  the PLATFORM account (the backend passes no `stripe_account`), so it must be
  confirmed with a platform-scoped instance -- `loadStripe(key)` with NO
  `stripeAccount` option. Confirming a platform SetupIntent on a connected
  instance returns "No such setupintent"; that is the live AdventHealth
  checkout failure recorded at CartCheckout.vue:1685. There is only one Stripe
  instance on this page, and it is the platform one, so the mistake is not
  available to make here.

  AMOUNTS COME FROM THE SERVER ON EVERY LOAD, never from the token. The page
  renders `remediation.amount_cents`, which the endpoint recomputes from the
  order; `amount_cents_at_mint` exists only to DETECT CHANGE. An order edited
  down to $0 after the link was sent shows "nothing to pay" and no card form.
-->
<template>
  <v-container data-testid="remediation-page" class="pa-4" style="max-width: 640px">
    <!-- LOADING -->
    <div v-if="state === 'loading'" data-testid="remediation-loading" class="text-center pa-8">
      <v-progress-circular indeterminate color="primary" />
      <p class="mt-4 text-body-2">Loading your order...</p>
    </div>

    <!-- DEAD LINK. Expired, revoked, consumed or unknown all arrive here:
         the API returns ONE opaque 404 for every token refusal so the
         response cannot be used to enumerate tokens. The page therefore
         cannot honestly distinguish them either, and says the useful thing
         instead of guessing. -->
    <v-card v-else-if="state === 'expired'" data-testid="remediation-expired" class="pa-6">
      <h2 class="text-h6 mb-3">This payment link is no longer valid</h2>
      <p class="text-body-2 mb-4">
        Payment links expire one week after they are sent, and each one can
        only be used once. If you still need to pay for this order, please
        contact the store and ask for a new link.
      </p>
    </v-card>

    <!-- LINK EXHAUSTED (#3963). The server caps how many card attempts one
         link may start; past it, /setup-intent refuses with
         too_many_attempts. Terminal: re-offering the form would only invite
         more refusals. -->
    <v-card
      v-else-if="state === 'link_exhausted'"
      data-testid="remediation-link-exhausted"
      class="pa-6"
    >
      <h2 class="text-h6 mb-3">This payment link can't be used again</h2>
      <p class="text-body-2">
        For your security, each payment link allows only a few card attempts.
        Please contact the store and ask for a new payment link.
      </p>
    </v-card>

    <!-- ALREADY HANDLED (P2-2, #3963). Another payment link for this order
         was completed first, so /confirm revoked this one and answered
         already_handled. Terminal, and nobody's fault: no form, no retry. -->
    <v-card
      v-else-if="state === 'already_handled'"
      data-testid="remediation-already-handled"
      class="pa-6"
    >
      <h2 class="text-h6 mb-3">This order is already taken care of</h2>
      <p class="text-body-2">
        Payment for this order has already been arranged, so there is nothing
        more to do here. No further action is needed.
      </p>
    </v-card>

    <!-- NOTHING OWED. The order was edited back down, or paid another way. -->
    <v-card
      v-else-if="state === 'already_paid'"
      data-testid="remediation-already-paid"
      class="pa-6"
    >
      <h2 class="text-h6 mb-3">This order is all paid up</h2>
      <p class="text-body-2">
        There is nothing to pay. If you were expecting a balance, the order
        may have been updated since your email was sent.
      </p>
    </v-card>

    <!-- SUCCESS -- charged now (the batch had already been released). -->
    <v-card
      v-else-if="state === 'success_charged'"
      data-testid="remediation-success-charged"
      class="pa-6"
    >
      <h2 class="text-h6 mb-3">Payment received</h2>
      <p class="text-body-2">
        Thank you -- we have charged {{ formatCents(amountCents) }} and your
        order is moving. A receipt is on its way to your email.
      </p>
    </v-card>

    <!-- SUCCESS -- card saved, charge happens at release. Deliberately NOT
         worded as "paid": nothing has been charged yet, and telling a shopper
         otherwise invites a dispute when the charge lands days later. -->
    <v-card
      v-else-if="state === 'success_pinned'"
      data-testid="remediation-success-pinned"
      class="pa-6"
    >
      <h2 class="text-h6 mb-3">Your card is saved</h2>
      <p class="text-body-2">
        Thank you. We have saved your card for this order and will charge
        {{ formatCents(amountCents) }} when this order goes into production
        with the rest of its batch. You do not need to do anything else.
      </p>
    </v-card>

    <!-- ACTIONABLE -->
    <v-card v-else class="pa-6">
      <h2 class="text-h6 mb-1">Complete your order</h2>
      <p class="text-body-2 mb-4">{{ leadCopy }}</p>

      <!-- CONSENT (Path B only). Three required elements: a plain statement
           that the order changed, both totals side by side, and an explicit
           affirmative control naming the amount. Not a passive disclosure. -->
      <v-alert
        v-if="requiresConsent"
        data-testid="remediation-consent-block"
        type="info"
        variant="tonal"
        class="mb-4"
      >
        <p class="text-body-2 mb-3">
          This order was updated after you placed it, so the amount has
          changed. Please review and approve the new total before paying.
        </p>
        <div class="d-flex justify-space-between text-body-2">
          <span>Original total</span>
          <span data-testid="remediation-old-total">
            {{ formatCents(expectedPriorCents) }}
          </span>
        </div>
        <div class="d-flex justify-space-between text-body-2 font-weight-bold">
          <span>New total</span>
          <span data-testid="remediation-new-total">
            {{ formatCents(amountCents) }}
          </span>
        </div>
        <v-checkbox
          v-model="consentGiven"
          data-testid="remediation-approve"
          class="mt-2"
          hide-details
          :label="`I approve the new total of ${formatCents(amountCents)}`"
        />
      </v-alert>

      <div v-else class="d-flex justify-space-between text-body-1 mb-4">
        <span>Amount due</span>
        <span data-testid="remediation-new-total">{{ formatCents(amountCents) }}</span>
      </div>

      <!-- CARD FORM. Mounted only once consent (if required) is given, so a
           shopper cannot enter a card against an amount they have not seen. -->
      <div v-if="consentSatisfied" data-testid="remediation-card-form">
        <div ref="cardMount" class="remediation-card-mount mb-4" />
      </div>

      <p v-if="errorMessage" data-testid="remediation-error" class="text-error text-body-2 mb-3">
        {{ errorMessage }}
      </p>

      <!-- TWO DISTINCT TESTIDS, not one control with a disabled attribute.
           The ui-runner's expect vocabulary has no attribute predicate, and
           two ids is the stronger assertion anyway: a MISSING control and an
           INERT one are indistinguishable to dom_absent, and only one of
           those is the design. -->
      <v-btn
        v-if="!consentSatisfied"
        data-testid="remediation-submit-disabled"
        color="primary"
        block
        disabled
      >
        {{ submitLabel }}
      </v-btn>
      <v-btn
        v-else
        data-testid="remediation-submit"
        color="primary"
        block
        :loading="state === 'submitting'"
        @click="submit"
      >
        {{ submitLabel }}
      </v-btn>

      <p class="text-caption text-medium-emphasis mt-3">
        This payment link expires one week after it was sent.
      </p>
    </v-card>
  </v-container>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";
import { resolvePublishableKey } from "@/services/stripePublishableKey";

export default {
  name: "StorefrontOrderPayment",

  data() {
    return {
      state: "loading",
      amountCents: 0,
      expectedPriorCents: null,
      requiresConsent: false,
      chargeTiming: "at_release",
      consentGiven: false,
      errorMessage: "",
      stripe: null,
      elements: null,
      cardElement: null,
      setupIntentId: null,
      // The STORE platform's publishable key, served by the API (null when
      // none is configured -- resolvePublishableKey then falls back).
      servedPublishableKey: null,
    };
  },

  computed: {
    token() {
      return this.$route.query.token || "";
    },
    orderId() {
      return this.$route.params.id;
    },
    /**
     * Path A needs no approval, so the card form is available immediately.
     * Path B gates it on the checkbox.
     */
    consentSatisfied() {
      return !this.requiresConsent || this.consentGiven;
    },
    leadCopy() {
      if (this.requiresConsent) {
        return "This order was updated and now has a balance. Approve the new amount and add a payment method to complete it.";
      }
      // Path A. Deliberately NOT "your card was declined" for Path B -- no
      // card was ever entered there, and telling a shopper to check a card
      // they never provided sends them to fix the wrong thing.
      return "We were not able to charge your card for this order. Add a payment method to complete it.";
    },
    submitLabel() {
      if (this.chargeTiming === "immediate") {
        return `Pay ${this.formatCents(this.amountCents)}`;
      }
      return `Save card for ${this.formatCents(this.amountCents)}`;
    },
  },

  watch: {
    // The card form only exists once consent is satisfied, so Stripe Elements
    // must mount after that flips -- not on load.
    consentSatisfied(now) {
      if (now) {
        this.$nextTick(() => this.mountCard());
      }
    },
  },

  async mounted() {
    await this.load();
  },

  methods: {
    formatCents(cents) {
      const n = Number(cents || 0) / 100;
      return `$${n.toFixed(2)}`;
    },

    async load() {
      // ``this.$axios``, NEVER a bare ``import axios``. The global instance
      // carries ``baseURL = process.env.API_BASE_URL`` (frontend/src/axios.js),
      // which on a sandbox is a DIFFERENT HOST AND PORT
      // (api.tf-sandbox.coules.io:5011) from the one serving the SPA.
      //
      // A bare axios call resolves relative to the PAGE, so
      // ``/order-remediation/340`` hit the frontend host and was answered by
      // the SPA's index.html with HTTP 200. The ``catch`` below then swallowed
      // the JSON parse failure and the page rendered its DEFAULT state: $0.00,
      // no consent block, and a LIVE submit button on an order that actually
      // owed $41.58 and required approval. It looked like a working page.
      //
      // Unit tests could not catch this -- they mock axios, so the baseURL
      // never matters. Found only by driving a real browser.
      if (!this.token) {
        this.state = "expired";
        return;
      }
      try {
        const { data } = await this.$axios.get(
          `/order-remediation/${this.orderId}`,
          { params: { token: this.token } }
        );
        const r = data.remediation || {};
        this.amountCents = r.amount_cents || 0;
        this.expectedPriorCents = r.expected_prior_cents;
        this.requiresConsent = !!r.requires_consent;
        this.chargeTiming = r.charge_timing || "at_release";
        this.servedPublishableKey = r.stripe_publishable_key || null;

        if (r.state === "already_paid") {
          this.state = "already_paid";
          return;
        }
        this.state = "actionable";
        if (this.consentSatisfied) {
          this.$nextTick(() => this.mountCard());
        }
      } catch (e) {
        // EVERY token refusal is the same opaque 404 by design, so there is
        // nothing to distinguish here. Anything else is also unrecoverable
        // from the shopper's side.
        this.state = "expired";
      }
    },

    async mountCard() {
      if (this.cardElement || !this.$refs.cardMount) return;
      // PLATFORM-SCOPED: no `stripeAccount` option. See the file header --
      // confirming a platform SetupIntent on a connected instance is the
      // production "No such setupintent" failure.
      //
      // And the RIGHT platform: the API creates the SetupIntent on the
      // store's platform (client_for(binding.mode)), so Stripe.js must load
      // that platform's key. The build-time STRIPE_PUBLIC_KEY is one key
      // for the whole deployment -- on a store it does not belong to, the
      // same "No such setupintent". Same resolver checkout uses (#3803):
      // the served key wins, the build-time value is only the fallback.
      this.stripe = await loadStripe(
        resolvePublishableKey({
          stripe_publishable_key: this.servedPublishableKey,
        })
      );
      if (!this.stripe) {
        this.errorMessage =
          "We could not load the payment form. Please refresh and try again.";
        return;
      }
      this.elements = this.stripe.elements();
      this.cardElement = this.elements.create("card");
      this.cardElement.mount(this.$refs.cardMount);
    },

    async submit() {
      this.errorMessage = "";
      const prior = this.state;
      this.state = "submitting";
      try {
        // 1. Ask the server for a SetupIntent. It STAGES the id on the
        //    payment link server-side -- the client never supplies it, and
        //    the card only becomes the order's authorization at /confirm.
        const { data: si } = await this.$axios.post(
          `/order-remediation/${this.orderId}/setup-intent`,
          { token: this.token }
        );

        // 2. Vault the card against the PLATFORM instance.
        const result = await this.stripe.confirmCardSetup(si.client_secret, {
          payment_method: { card: this.cardElement },
        });
        if (result.error) {
          this.errorMessage = result.error.message;
          this.state = prior;
          return;
        }

        // 3. Confirm server-side. The consented amount is sent BACK so the
        //    server can check it against live state -- that round trip is the
        //    whole point of the consent step: approval bound to a number.
        const body = {
          token: this.token,
          setup_intent_id: si.setup_intent_id,
        };
        if (this.requiresConsent) {
          body.consented_amount_cents = this.amountCents;
        }
        const { data: done } = await this.$axios.post(
          `/order-remediation/${this.orderId}/confirm`,
          body
        );

        if (done.outcome === "charged") {
          this.state = "success_charged";
        } else if (done.outcome === "already_paid") {
          this.state = "already_paid";
        } else {
          this.state = "success_pinned";
        }
      } catch (e) {
        const payload = e?.response?.data || {};
        if (payload.error === "already_paid") {
          // Paid through another link (or the release) since this page
          // loaded. Stop offering a card form; there is nothing to retry.
          this.state = "already_paid";
          return;
        }
        if (payload.error === "already_handled") {
          // Another link for this order won; the server revoked this one.
          this.state = "already_handled";
          return;
        }
        if (payload.error === "too_many_attempts") {
          this.state = "link_exhausted";
          return;
        }
        if (payload.error === "amount_changed") {
          // The order moved between load and submit. Re-read and re-consent
          // rather than charging a number the shopper has not seen.
          this.consentGiven = false;
          this.errorMessage = payload.message || "This order's total changed.";
          await this.load();
          return;
        }
        this.errorMessage =
          payload.message ||
          "We could not complete that payment. Please try again.";
        this.state = prior;
      }
    },
  },
};
</script>

<style scoped>
.remediation-card-mount {
  border: 1px solid #cfd7df;
  border-radius: 4px;
  padding: 12px;
}
</style>
